import { FC, ReactNode, useRef, type CSSProperties } from 'react';
import { Menu } from '@pypestream/design-system';
import { useVirtualizer } from '@tanstack/react-virtual';

import type { WorkflowError } from '../../stores/table-store';
import {
  buildWindowedRowStyle,
  errorIdentityKey,
  getErrorListLayout,
} from './validation-popover.utils';

/** What a row needs in order to sit in a virtual window: its position, and the
 *  size/position of the full list, which AT cannot infer from a DOM holding
 *  only the window. Absent entirely in the unwindowed case.
 *
 *  There is deliberately no measurement ref here (PSTUDIO-4119): a windowed row
 *  is pinned to the estimate, so nothing measures it and nothing observes it. */
export type WindowedRowProps = {
  key: string;
  setSize: number;
  posInSet: number;
  style: CSSProperties;
};

/**
 * The windowed error list (PSTUDIO-3990 review defect 6).
 *
 * Neither node-number error class is capped in the store — a cap would stop the
 * cell highlights past it while the publish toast still says "fix the
 * highlighted cells" — so a RAW paste of a blank `Node #` column over thousands
 * of rows produces thousands of errors, and the list used to mount one Lit
 * `ps-graph-node` per error: clicking the badge froze the main thread.
 *
 * Windowed with `@tanstack/react-virtual`, the SAME virtualizer the workflows
 * table uses (`components/table/table.tsx`) — no new dependency, and no
 * hand-rolled windowing. The arithmetic lives in `getErrorListLayout` and
 * `buildWindowedRowStyle` because that is the only part a node-environment unit
 * test can reach.
 *
 * The rows are NOT measured (PSTUDIO-4119). Measuring them is what made a
 * measure → notify → re-render loop reachable: the height came from
 * `getBoundingClientRect()`, `resizeItem` compares with a strict `delta !== 0`,
 * and a sub-pixel value that flips between repaints therefore never let the
 * cycle close — React hit its nested-update limit, threw #185, and the app-wide
 * boundary replaced the whole Workflows route with "Reload Page". So a windowed
 * row is pinned to the estimate (as it always was on Firefox) and no
 * `measureElement` is passed at all.
 *
 * A row must stay a DIRECT slotted child of `ps-menu`. `MenuWC.getAllItems()`
 * reads `slot.assignedElements({flatten:true})` and keeps only `role="menuitem"`
 * entries, and `setCurrentItem()` — driven from that list — is the ONLY code
 * anywhere that puts `tabindex` on a `ps-graph-node` (the pattern never sets one
 * itself). Wrapping rows in positioning `<div>`s therefore emptied that list and
 * took Tab, the arrow keys and Enter away from the whole error list — a
 * keyboard regression introduced by a change made FOR accessibility. So the
 * scroller lives OUTSIDE the menu, and `ps-menu` itself is the sizer: it is
 * `display:block; position:relative` already (`menu.scss`), so absolutely
 * positioned slotted children resolve against it.
 */
export const ErrorList: FC<{
  errors: WorkflowError[];
  renderRow: (
    error: WorkflowError,
    idx: number,
    windowed?: WindowedRowProps
  ) => ReactNode;
  testId: string;
}> = ({ errors, renderRow, testId }) => {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const { height, totalHeight, rowEstimate, overscan, shouldVirtualize } =
    getErrorListLayout({ count: errors.length });

  const virtualizer = useVirtualizer({
    count: errors.length,
    getScrollElement: () => scrollerRef.current,
    estimateSize: () => rowEstimate,
    // No `getItemKey` on purpose. The virtualizer only uses it to key its
    // measurement caches (`itemSizeCache` / `elementsCache`), and this list is
    // never measured — so passing one bought nothing and cost correctness: an
    // inline arrow re-walked the whole list on every render (~3.4k key builds at
    // 3362 errors, because `react-virtual` calls `setOptions` per render and
    // `virtual-core` memoises on that function's identity), while a stabilised
    // one made the keys STALE — `getMeasurements` is memoised on
    // `[count, …, getItemKey]`, so replacing entries without changing the count
    // kept handing out the previous keys (measured). The React key is built at
    // the point of use below instead, where it is always current.
    overscan,
    // No `measureElement` on purpose (PSTUDIO-4119) — see the note above. Rows
    // are pinned to `rowEstimate`, so the estimate IS the height and there is
    // nothing to measure.
  });

  // The ordinary case: every error is in the DOM, `ps-menu` sees them all, and
  // roving works across the whole list exactly as it did before windowing.
  if (!shouldVirtualize) {
    return (
      <Menu data-test-id={testId}>
        {errors.map((error, idx) => renderRow(error, idx))}
      </Menu>
    );
  }

  // Both heights are ARITHMETIC, never `virtualizer.getTotalSize()`
  // (PSTUDIO-4119). Stated at its real strength: the virtualizer does put a
  // `ResizeObserver` on this very scroller (`getScrollElement`), so sourcing the
  // scroller's height from the virtualizer's own total was a path from a
  // measurement back into an observed element — but it was a NARROW one, since
  // `observeElementRect` rounds the rect it reports and any list past the
  // threshold pinned the scroller at `ERROR_LIST_MAX_HEIGHT` in both extremes
  // anyway. The loop went through the ROWS; this is the hygiene that removes the
  // remaining feedback edge, not the fix. With the rows pinned, these two
  // numbers are exactly what a measured total would be.
  return (
    <div
      ref={scrollerRef}
      data-test-id={testId}
      role="presentation"
      style={{ height, overflowY: 'auto', overflowX: 'hidden' }}
    >
      <Menu
        style={{ display: 'block', height: totalHeight, position: 'relative' }}
      >
        {virtualizer.getVirtualItems().map((item) =>
          renderRow(errors[item.index], item.index, {
            // Keyed by the entry's own identity, not by its position, so a
            // row's React key follows the ERROR: fixing one error shifts every
            // entry below it, and an index key would hand each of them the
            // identity of a different error (PSTUDIO-3990).
            key: errorIdentityKey(errors[item.index]),
            // Only the window is in the DOM, so AT would otherwise announce
            // "3 of 19" for a list of three thousand.
            setSize: errors.length,
            posInSet: item.index + 1,
            style: buildWindowedRowStyle({
              start: item.start,
              rowEstimate,
            }),
          })
        )}
      </Menu>
    </div>
  );
};
